import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  ArrowRight,
  ArrowLeft,
  Waves,
  ListOrdered,
  Clock,
  Plus,
  X
} from 'lucide-react';

/**
 * HP.5 Guided Input: Overwhelm triage
 * 2 short steps so users can act under pressure:
 *   1. Describe the overwhelm (free-form)
 *   2. Name 2-5 pressures + deadline context
 */

const STEPS = [
  { id: 'situation', title: "What's going on?", subtitle: 'Describe the overwhelm in your own words', icon: Waves },
  { id: 'pressures', title: 'Top pressures', subtitle: 'Name the things pulling on you right now', icon: ListOrdered }
];

const OverwhelmGuidedInput = ({ onComplete, onCancel, accentColor = '#14b8a6' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    situation_text: '',
    top_pressures: ['', ''],
    deadlines_context: ''
  });

  const updateField = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const updatePressure = (idx, value) => {
    const next = [...formData.top_pressures];
    next[idx] = value;
    updateField('top_pressures', next);
  };

  const addPressure = () => {
    if (formData.top_pressures.length >= 5) return;
    updateField('top_pressures', [...formData.top_pressures, '']);
  };

  const removePressure = (idx) => {
    const next = formData.top_pressures.filter((_, i) => i !== idx);
    updateField('top_pressures', next.length ? next : ['']);
  };

  const canAdvance = () => {
    if (currentStep === 0) return formData.situation_text.trim().length >= 10;
    return formData.top_pressures.filter((p) => p.trim()).length >= 1;
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) setCurrentStep((s) => s + 1);
    else onComplete({
      situation_text: formData.situation_text.trim(),
      top_pressures: formData.top_pressures.map((p) => p.trim()).filter(Boolean),
      deadlines_context: formData.deadlines_context.trim()
    });
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
    else onCancel?.();
  };

  const Step = STEPS[currentStep];
  const StepIcon = Step.icon;

  return (
    <div data-testid="overwhelm-guided-input" className="max-w-2xl mx-auto">
      {/* Progress */}
      <div className="mb-8 flex items-center justify-center gap-2">
        {STEPS.map((s, i) => (
          <div
            key={s.id}
            className="h-1.5 w-16 rounded-full transition-colors"
            style={{ backgroundColor: i <= currentStep ? accentColor : '#1f2937' }}
          />
        ))}
      </div>

      {/* Header */}
      <div className="mb-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ backgroundColor: `${accentColor}20` }}>
          <StepIcon className="w-6 h-6" style={{ color: accentColor }} />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">{Step.title}</h2>
        <p className="text-gray-400 text-sm">{Step.subtitle}</p>
      </div>

      {/* Step content */}
      <div className="mb-8">
        {currentStep === 0 ? (
          <textarea
            data-testid="overwhelm-situation-input"
            value={formData.situation_text}
            onChange={(e) => updateField('situation_text', e.target.value)}
            placeholder="E.g. I have three major projects colliding next week, my manager keeps adding requests, and I'm already behind on the thing I said I'd deliver on Friday..."
            className="w-full min-h-[180px] bg-gray-900/60 border border-gray-800 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-teal-500"
            maxLength={2000}
            autoFocus
          />
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2 font-medium">
                Pressures on you right now <span className="text-gray-500 font-normal">(2–5)</span>
              </label>
              <div className="space-y-2">
                {formData.top_pressures.map((pressure, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm w-5">{idx + 1}.</span>
                    <input
                      data-testid={`overwhelm-pressure-${idx}`}
                      type="text"
                      value={pressure}
                      onChange={(e) => updatePressure(idx, e.target.value)}
                      placeholder={idx === 0 ? 'e.g. Q3 board prep' : idx === 1 ? 'e.g. New hire onboarding' : 'e.g. Production incident follow-up'}
                      className="flex-1 bg-gray-900/60 border border-gray-800 rounded-lg px-3 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-teal-500 text-sm"
                      maxLength={120}
                    />
                    {formData.top_pressures.length > 1 ? (
                      <button
                        onClick={() => removePressure(idx)}
                        className="w-8 h-8 rounded-lg bg-gray-900 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-colors flex items-center justify-center"
                        aria-label="Remove pressure"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    ) : null}
                  </div>
                ))}
                {formData.top_pressures.length < 5 ? (
                  <button
                    onClick={addPressure}
                    className="flex items-center gap-1.5 text-sm text-teal-400 hover:text-teal-300 ml-7"
                    data-testid="overwhelm-add-pressure"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add another
                  </button>
                ) : null}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2 font-medium flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-gray-500" /> Deadlines & timing <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <input
                data-testid="overwhelm-deadlines-input"
                type="text"
                value={formData.deadlines_context}
                onChange={(e) => updateField('deadlines_context', e.target.value)}
                placeholder="E.g. Two overlap this week; one is end of month"
                className="w-full bg-gray-900/60 border border-gray-800 rounded-lg px-3 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-teal-500 text-sm"
                maxLength={500}
              />
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="outline"
          onClick={handleBack}
          className="border-gray-700 text-gray-300 hover:text-white"
          data-testid="overwhelm-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Button
          onClick={handleNext}
          disabled={!canAdvance()}
          className="text-white font-semibold px-6"
          style={{ backgroundColor: canAdvance() ? accentColor : '#374151' }}
          data-testid="overwhelm-next-btn"
        >
          {currentStep === STEPS.length - 1 ? 'Get clarity' : 'Continue'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default OverwhelmGuidedInput;
